"use client";
// Renderar Atom[] som klickbara JSX-spans. Stilen mimar matematisk typografi
// (KaTeX-likt) men är skriven från scratch så vi kan haka onClick på varje
// nod. KaTeX används bara om vi någon gång vill rendera "frusen" output.

import { Fragment, type CSSProperties } from "react";
import type { Atom, Cursor } from "./types";
import { pathSeg } from "./manipulate";

interface RenderCtx {
  cursor: Cursor;
  onCursorTo: (cursor: Cursor) => void;
}

const CURSOR_STYLE: CSSProperties = {
  display: "inline-block",
  width: 0,
  height: "1.2em",
  verticalAlign: "middle",
  borderLeft: "2px solid var(--color-accent)",
  marginLeft: "-1px",
  marginRight: "-1px",
  animation: "calcCursorBlink 1s steps(2) infinite",
};

function CursorBar() {
  return <span style={CURSOR_STYLE} aria-hidden="true" />;
}

function isCursorHere(
  cursor: Cursor,
  path: string[],
  index: number,
): boolean {
  if (cursor.index !== index) return false;
  if (cursor.path.length !== path.length) return false;
  for (let i = 0; i < path.length; i++) {
    if (cursor.path[i] !== path[i]) return false;
  }
  return true;
}

interface ListProps {
  list: Atom[];
  path: string[];
  ctx: RenderCtx;
  /** Tom-list-placeholder: visa litet streck om listan är tom så slot:en
   *  syns och kan klickas. */
  placeholder?: string;
}

export function RenderList({ list, path, ctx, placeholder }: ListProps) {
  if (list.length === 0) {
    const here = isCursorHere(ctx.cursor, path, 0);
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          ctx.onCursorTo({ path, index: 0 });
        }}
        style={{
          display: "inline-block",
          // Absolute units so placeholders förblir lika stora i sup/sub-kontext
          // där em-baserad sizing skulle krympa dem (font-size: 0.7em).
          minWidth: "1.5rem",
          minHeight: "2rem",
          padding: "0 2px",
          opacity: here ? 1 : 0.5,
        }}
      >
        {here && <CursorBar />}
        {!here && (placeholder ?? "")}
      </span>
    );
  }
  return (
    <>
      {list.map((atom, i) => (
        <Fragment key={atom.id}>
          {isCursorHere(ctx.cursor, path, i) && <CursorBar />}
          <RenderAtom atom={atom} path={path} index={i} ctx={ctx} />
        </Fragment>
      ))}
      {isCursorHere(ctx.cursor, path, list.length) && <CursorBar />}
      {/* Tail-klickyta så man kan placera cursor efter sista atomet — bara
          en hårfin span eftersom större bredd skapar synligt gap mellan
          base och exp i sup-rendering. */}
      <span
        onClick={(e) => {
          e.stopPropagation();
          ctx.onCursorTo({ path, index: list.length });
        }}
        style={{ display: "inline-block", minWidth: "0.05em" }}
      />
    </>
  );
}

interface AtomProps {
  atom: Atom;
  path: string[];
  index: number;
  ctx: RenderCtx;
}

function RenderAtom({ atom, path, index, ctx }: AtomProps) {
  // Klick på atom positionerar cursor *efter* atomen så backspace tar bort
  // den (matchar text-editor-konvention där markören står efter klick-positionen).
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    ctx.onCursorTo({ path, index: index + 1 });
  };
  switch (atom.kind) {
    case "num":
      return (
        <span style={{ fontVariantNumeric: "tabular-nums" }} onClick={onClick}>
          {atom.v}
        </span>
      );
    case "op": {
      const sym =
        atom.v === "+" ? "+" : atom.v === "-" ? "−" : atom.v === "*" ? "·" : "/";
      return (
        <span
          onClick={onClick}
          style={{ margin: "0 0.18em", color: "var(--color-text)" }}
        >
          {sym}
        </span>
      );
    }
    case "const":
      return (
        <span onClick={onClick} style={{ fontStyle: "italic" }}>
          {atom.name === "pi" ? "π" : "e"}
        </span>
      );
    case "comma":
      return <span onClick={onClick}>,&thinsp;</span>;
    case "paren": {
      const innerPath = [...path, pathSeg(atom.id, null)];
      return (
        <span onClick={onClick}>
          <span style={{ color: "var(--color-text-muted)" }}>(</span>
          <RenderList list={atom.inner} path={innerPath} ctx={ctx} />
          <span style={{ color: "var(--color-text-muted)" }}>)</span>
        </span>
      );
    }
    case "abs": {
      const innerPath = [...path, pathSeg(atom.id, null)];
      return (
        <span onClick={onClick}>
          <span style={{ color: "var(--color-text-muted)" }}>|</span>
          <RenderList list={atom.inner} path={innerPath} ctx={ctx} />
          <span style={{ color: "var(--color-text-muted)" }}>|</span>
        </span>
      );
    }
    case "sqrt": {
      const innerPath = [...path, pathSeg(atom.id, null)];
      return (
        <span
          onClick={onClick}
          style={{ display: "inline-flex", alignItems: "flex-start" }}
        >
          <span style={{ fontSize: "1.2em", color: "var(--color-text)" }}>
            √
          </span>
          <span
            style={{
              borderTop: "1px solid var(--color-text)",
              paddingTop: "1px",
              marginTop: "0.05em",
            }}
          >
            <RenderList list={atom.inner} path={innerPath} ctx={ctx} placeholder="□" />
          </span>
        </span>
      );
    }
    case "nthroot": {
      const degPath = [...path, pathSeg(atom.id, 0)];
      const innerPath = [...path, pathSeg(atom.id, 1)];
      return (
        <span
          onClick={onClick}
          style={{ display: "inline-flex", alignItems: "flex-start" }}
        >
          <span
            style={{
              fontSize: "0.7em",
              transform: "translateY(-0.2em)",
              marginRight: "-0.2em",
            }}
          >
            <RenderList list={atom.degree} path={degPath} ctx={ctx} placeholder="n" />
          </span>
          <span style={{ fontSize: "1.2em", color: "var(--color-text)" }}>
            √
          </span>
          <span
            style={{
              borderTop: "1px solid var(--color-text)",
              paddingTop: "1px",
              marginTop: "0.05em",
            }}
          >
            <RenderList list={atom.inner} path={innerPath} ctx={ctx} placeholder="□" />
          </span>
        </span>
      );
    }
    case "frac": {
      const numPath = [...path, pathSeg(atom.id, 0)];
      const denPath = [...path, pathSeg(atom.id, 1)];
      return (
        <span
          onClick={onClick}
          style={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            verticalAlign: "middle",
            margin: "0 0.1em",
            fontSize: "0.95em",
          }}
        >
          <span style={{ padding: "0 0.15em" }}>
            <RenderList list={atom.num} path={numPath} ctx={ctx} placeholder="□" />
          </span>
          <span
            style={{
              borderTop: "1px solid var(--color-text)",
              alignSelf: "stretch",
            }}
          />
          <span style={{ padding: "0 0.15em" }}>
            <RenderList list={atom.den} path={denPath} ctx={ctx} placeholder="□" />
          </span>
        </span>
      );
    }
    case "sup": {
      const basePath = [...path, pathSeg(atom.id, 0)];
      const expPath = [...path, pathSeg(atom.id, 1)];
      return (
        <span onClick={onClick}>
          <RenderList list={atom.base} path={basePath} ctx={ctx} placeholder="□" />
          <span
            style={{
              fontSize: "0.7em",
              verticalAlign: "super",
              lineHeight: 0,
              marginLeft: "0.04em",
            }}
          >
            <RenderList list={atom.exp} path={expPath} ctx={ctx} placeholder="□" />
          </span>
        </span>
      );
    }
    case "fn": {
      const argPath = [...path, pathSeg(atom.id, 0)];
      const labelMap: Record<string, string> = {
        sin: "sin",
        cos: "cos",
        tan: "tan",
        asin: "sin⁻¹",
        acos: "cos⁻¹",
        atan: "tan⁻¹",
        ln: "ln",
        lg: "lg",
        log: "log",
      };
      return (
        <span onClick={onClick}>
          <span style={{ marginRight: "0.05em" }}>{labelMap[atom.name]}</span>
          <span style={{ color: "var(--color-text-muted)" }}>(</span>
          <RenderList list={atom.arg} path={argPath} ctx={ctx} placeholder="□" />
          <span style={{ color: "var(--color-text-muted)" }}>)</span>
        </span>
      );
    }
    case "logbase": {
      const basePath = [...path, pathSeg(atom.id, 0)];
      const argPath = [...path, pathSeg(atom.id, 1)];
      return (
        <span onClick={onClick}>
          <span>log</span>
          <span style={{ fontSize: "0.7em", verticalAlign: "sub", lineHeight: 0 }}>
            <RenderList list={atom.base} path={basePath} ctx={ctx} placeholder="a" />
          </span>
          <span style={{ color: "var(--color-text-muted)", marginLeft: "0.1em" }}>
            (
          </span>
          <RenderList list={atom.arg} path={argPath} ctx={ctx} placeholder="□" />
          <span style={{ color: "var(--color-text-muted)" }}>)</span>
        </span>
      );
    }
    case "fact": {
      const innerPath = [...path, pathSeg(atom.id, null)];
      return (
        <span onClick={onClick}>
          <RenderList list={atom.inner} path={innerPath} ctx={ctx} placeholder="n" />
          <span style={{ color: "var(--color-text)" }}>!</span>
        </span>
      );
    }
    case "binom": {
      const nPath = [...path, pathSeg(atom.id, 0)];
      const rPath = [...path, pathSeg(atom.id, 1)];
      const label = atom.binomTyp === "ncr" ? "C" : "P";
      return (
        <span onClick={onClick}>
          <span>{label}</span>
          <span style={{ color: "var(--color-text-muted)" }}>(</span>
          <RenderList list={atom.n} path={nPath} ctx={ctx} placeholder="n" />
          <span style={{ color: "var(--color-text-muted)" }}>, </span>
          <RenderList list={atom.r} path={rPath} ctx={ctx} placeholder="r" />
          <span style={{ color: "var(--color-text-muted)" }}>)</span>
        </span>
      );
    }
  }
}
